import { useMemo, useState } from 'react'
import { Breadcrumb, Modal, ProgressIndicator } from '@/components/atoms'
import { PageHeader, SaveCancelFooter } from '@/components/molecules'
import { ActivitySetUp } from '@/containers'
import { isNotEmptyAndDefined } from '@/helpers/shadcn/utils'
import { useAppSelector } from '@/store'
import { resetActivityData, setActivityFormData } from '@/store/slices/activitySlice'
import { ACTIVITY_TYPE } from '@/types/Activity'
import { MODAL_TYPE } from '@/utils/constants/globalStyle'
import { ROUTES } from '@/utils/constants/routesConstants'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook'
import { COUNTRY_CODES_FREQUENCY } from '@/utils/constants/globalConstants'
import { DATE_VALIDATION_TYPE} from '@/utils/constants/commonConstants'
import { getDisplayCurrency, isPublished } from '@/utils/commons'
import { useActivityValidation } from '@/hooks/useActivityValidation'

import { useDatePickerInput } from '@/components/atoms/DatePickerInput/datePickerInput-hook'
const AddActivity = () => {
  const [isCancel, setIsCancel] = useState(false)
  const { formData } = useSelector(state => state.activity)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const isCountryCodeFrequency = useMemo(() => COUNTRY_CODES_FREQUENCY.includes(countryCode), [countryCode]);
  const localCurrencyCode = getDisplayCurrency(countryCode)
  const { handleDraft: handleReviewDraft,handleValidationModalClose,isNameValid,getActivityName } = useActivityReview()
  
  const {
    handleActivityDurationValidation,
    resetValidation,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
  } = useActivityValidation()

  const {
    pointsEarned,
    pointsEarnedValid,
    activityType,
    hasEndDate,
    startDate,
    endDate,
    dailyLoginLimit,
    dailyLoginLimitValid,
    allTimeLoginLimit,
    allTimeLoginLimitValid,
    startDateValid,
    endDateValid,
    activityName,
    orderType,
    localCurrency,
    localCurrencyValid,
    numberOfCases,
    numberOfCasesValid,
    receivePoints,
    status
  } = formData

  const isPublishedStatus = isPublished(status)

  const {validateDates: validateStartDate} = useDatePickerInput('startDate',   [
    {type: 'required', msg: ` is required`},
    {type: DATE_VALIDATION_TYPE.START_DATE, msg: ` can't be in the past.`, endDate: endDate},
    {type: DATE_VALIDATION_TYPE.START_DATE_TODAY, msg: `  Target can only start from a date in future`},  ])
  const {validateDates: validateEndDate} = useDatePickerInput('endDate', [
    {type: 'required', msg: ` is required`},   
    {type: DATE_VALIDATION_TYPE.START_DATE, msg: ` can't be in the past.`},
    {type: DATE_VALIDATION_TYPE.END_DATE, msg: ` can't be less than Start date.`, startDate: startDate},
  ])
  // Order alignment for Welcome review page
  const isValidStartDate = (date) => {
    if(!date)
        return false
    const d = validateStartDate(date);
    return d === undefined || d === null;
  }

  const isValidEndDate = (date) => { 
    if(!date)
        return false
    const d = validateEndDate(date);
    return d === undefined || d === null;
  }
  const welcomeActivityOrder = {
    activityType: activityType,
    pointsEarned: pointsEarned,
    hasEndDate: hasEndDate,
    startDate: startDate,
    ...(formData.name && {name: formData.name}),
    ...(formData.activityName && {activityName: formData.activityName}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id}),
    ...(hasEndDate && {endDate: endDate}),
  }

  // Order alignment for Login review page
  const loginActivityOrder = {
    activityType: activityType,
    pointsEarned: pointsEarned,
    ...(dailyLoginLimit && {dailyLoginLimit: dailyLoginLimit}),
    ...(allTimeLoginLimit && {allTimeLoginLimit: allTimeLoginLimit}),
    startDate: startDate,
    endDate: endDate,
    receivePoints: receivePoints,
    ...(formData.name && {name: formData.name}),
    ...(formData.activityName && {activityName: formData.activityName}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id}),
  }

  const pointsOnOrderDeliveryActivityOrder = () => {
    const fieldsArranged = {
      activityType: activityType,
      activityName: activityName,
      orderType: orderType,
      ...(formData.name && {name: formData.name}),
      ...(formData.uuid && {uuid: formData.uuid}),
      ...(formData.status && {status: formData.status}),
      ...(formData.id && {id: formData.id}),
    }
    if (isCountryCodeFrequency) {
      fieldsArranged.numberOfCases = numberOfCases
      fieldsArranged.pointsEarned = pointsEarned
    } else {
      fieldsArranged.localCurrency = localCurrency
      fieldsArranged.pointsEarned = pointsEarned
    }
    fieldsArranged.startDate = startDate
    fieldsArranged.endDate = endDate
    return fieldsArranged
  }

  const handleDraft = event => {
    event.stopPropagation()
    handleReviewDraft()
  }


  const handleNext = async () => {
    const uniqueActivityName = getActivityName()
    const formDataWithOrder = getFormDataWithOrder();
    dispatch(setActivityFormData(formDataWithOrder));
    const isEditingPublished = isPublished(formData?.status) && formData?.id;
    // Skip duration validation for PRODUCT_PURCHASE activity type
    if (activityType !== ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      let validationParams = {...formData, activityName: uniqueActivityName}
      if(!isEditingPublished && formData?.id){
        delete validationParams.id;
      }
      const isDurationValid = await handleActivityDurationValidation(validationParams);
      if (!isDurationValid) {
        // Validation failed, toast is already shown by the hook
        return;
      }
    }
    
    // Validation passed - reset any existing toast state and navigate
    resetValidation();
    navigate(ROUTES.ACTIVITY_REVIEW);
  };
  const handleCancel = () => {
    setIsCancel(!isCancel)
  }

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidLoginDataForEdit = ({ validateStartDate = true }) => {
    const isStartDateInvalid = !isNotEmptyAndDefined(startDate)
    const validStartDate = validateStartDate ? isValidStartDate(startDate) : true
    const isEndDateInvalid = !isNotEmptyAndDefined(endDate)
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    const isReceivePointsInvalid = !isNotEmptyAndDefined(receivePoints)

    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsEarnedError = pointsEarnedValid === true
    const hasDailyLoginLimitError = dailyLoginLimitValid === true
    const hasAllTimeLoginLimitError = allTimeLoginLimitValid === true
    // const hasEndDateError = endDateValid === true || endDateValid === undefined
    const validEndDate = isValidEndDate(endDate)
    return (
      !validStartDate ||
      hasPointsEarnedError ||
      hasDailyLoginLimitError ||
      hasAllTimeLoginLimitError ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      isReceivePointsInvalid ||
      isPointsEarnedInvalid ||
      !validEndDate
    )
  }

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidLoginDataForCreation = ({ validateStartDate = true }) => {
    const isStartDateInvalid = !isNotEmptyAndDefined(startDate)
    const validStartDate = validateStartDate ? isValidStartDate(startDate) : true
    const isEndDateInvalid = !isNotEmptyAndDefined(endDate)
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    const isReceivePointsInvalid = !isNotEmptyAndDefined(receivePoints)

    // Stricter validation - check for validation errors AND undefined states
    const hasPointsEarnedError = pointsEarnedValid === true
    const hasDailyLoginLimitError = dailyLoginLimitValid === true
    const hasAllTimeLoginLimitError = allTimeLoginLimitValid === true
    const validEndDate = isValidEndDate(endDate)
    return (
      hasPointsEarnedError ||
      !validStartDate ||
      hasDailyLoginLimitError ||
      hasAllTimeLoginLimitError ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      isReceivePointsInvalid ||
      isPointsEarnedInvalid ||
      !validEndDate
    )
  }

  const isInvalidLoginData = ({ validateStartDate = true }) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode =  formData.uuid || formData.status || formData.activityName
    if (isEditMode) {
      return isInvalidLoginDataForEdit({ validateStartDate })
    } else {
      return isInvalidLoginDataForCreation({ validateStartDate })
    }
  }

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidWelcomeDataForEdit = ({ validateStartDate = true }) => {
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    const isStartDateMissing = !isNotEmptyAndDefined(startDate)
    const validStartDate = validateStartDate ? isValidStartDate(startDate) : true
    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid
    const hasStartDateError = startDateValid === true
    const hasEndDateError = endDateValid === true

    if (hasEndDate) {
      const isEndDateMissing = !isNotEmptyAndDefined(endDate)
      return !validStartDate || hasPointsError || hasStartDateError || hasEndDateError || isStartDateMissing || isEndDateMissing
    }

    return  !validStartDate || hasPointsError || hasStartDateError || isStartDateMissing
  }

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidWelcomeDataForCreation = ({ validateStartDate = true }) => {
    const isStartDateInvalid = validateStartDate ? validateDate(startDateValid) : true
    const isEndDateInvalid = validateDate(endDateValid)
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    const isStartDateMissing = !isNotEmptyAndDefined(startDate)

    // Check if points earned is invalid (either validation error or empty value)
    const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid

    if (hasEndDate) {
      const isEndDateMissing = !isNotEmptyAndDefined(endDate)
      return hasPointsError || isStartDateInvalid || isEndDateInvalid || isStartDateMissing || isEndDateMissing
    }

    return hasPointsError || isStartDateInvalid || isStartDateMissing
  }

  const isInvalidWelcomeData = ({ validateStartDate = true }) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode = formData.activityName || formData.uuid || formData.status

    if (isEditMode) {
      return isInvalidWelcomeDataForEdit({ validateStartDate })
    } else {
      return isInvalidWelcomeDataForCreation({ validateStartDate })
    }
  }

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidPointsOrderDeliverForEdit = ({ validateStartDate = true }) => {
    const isStartDateMissing = !isNotEmptyAndDefined(startDate) 
    const validStartDate = validateStartDate ? isValidStartDate(startDate) : true
    const isEndDateMissing = !isNotEmptyAndDefined(endDate)
    const isCurrencyFrequencyValid = isCountryCodeFrequency
      ? numberOfCasesValid === true || !isNotEmptyAndDefined(numberOfCases)
      : localCurrencyValid === true || !isNotEmptyAndDefined(localCurrency)

    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsEarnedError = pointsEarnedValid === true
    const hasStartDateError = startDateValid === true
    const hasEndDateError = endDateValid === true

    return (
      hasPointsEarnedError ||
      !validStartDate ||
      isCurrencyFrequencyValid ||
      !isNotEmptyAndDefined(pointsEarned) ||
      !isNotEmptyAndDefined(activityName) ||
      hasStartDateError ||
      hasEndDateError ||
      isStartDateMissing ||
      isEndDateMissing ||
      !isNotEmptyAndDefined(orderType)
    )
  }

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidPointsOrderDeliverForCreation = ({ validateStartDate = true }) => {
    const isStartDateInvalid = validateStartDate ? validateDate(startDateValid) : true
    const isEndDateInvalid = !isStartDateInvalid && formData.endDate === undefined
    const isCurrencyFrequencyValid = isCountryCodeFrequency
      ? numberOfCasesValid || !isNotEmptyAndDefined(numberOfCases)
      : localCurrencyValid || !isNotEmptyAndDefined(localCurrency)
    const hasPointsEarnedError = pointsEarnedValid === true
    return (
      hasPointsEarnedError ||
      isCurrencyFrequencyValid ||
      !isNotEmptyAndDefined(pointsEarned) ||
      !isNotEmptyAndDefined(activityName) ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      !isNotEmptyAndDefined(orderType)
    )
  }

  const isInvalidPointsOrderDeliver = ({ validateStartDate = true }) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode = formData.activityName || formData.uuid || formData.status

    if (isEditMode) {
      return isInvalidPointsOrderDeliverForEdit({ validateStartDate })
    } else {
      return isInvalidPointsOrderDeliverForCreation({ validateStartDate })
    }
  }

  const validateDate = dateValid => {
    // Only return true (invalid) if there's an actual validation error
    // undefined means not validated yet, which should be treated as valid for pre-filled data
    return dateValid === true
  }

  const validateFormData = ({ validateStartDate = true }) => {
    if (activityType === ACTIVITY_TYPE.LOGIN) {
      return isInvalidLoginData({ validateStartDate })
    } else if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return isInvalidWelcomeData({ validateStartDate })
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return isInvalidPointsOrderDeliver({ validateStartDate })
    } else if (handleHideSaveDraft()) {
      return true
    }
  }

  // Remove keys if they are empty
  const getFormDataWithOrder = () => {
    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return {...welcomeActivityOrder, name: formData.name, status: formData.status, uuid: formData.uuid, id: formData.id}
    } else if (activityType === ACTIVITY_TYPE.LOGIN) {
      return {...loginActivityOrder, name: formData.name, status: formData.status, uuid: formData.uuid, id: formData.id}
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return {
        ...pointsOnOrderDeliveryActivityOrder(),
        name: formData.name,
        status: formData.status,
        uuid: formData.uuid,
        id: formData.id,
      }
    }
  }

  const onClickPrimaryButton = () => {
    handleResetFormData()
    navigate(ROUTES.SALES_TARGETS + '?tab=0')
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false,
    }
    dispatch(resetActivityData(defaultFormData))
  }

  const handleHideSaveDraft = () => {
    return formData.activityType === '' || formData.activityType === undefined
  }

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: '/',
              label: '',
            },

            {
              href: '',
              label: 'Add Activity',
            },
          ],
        }}
        id={'aa_bradcrumb'}
      />

      <PageHeader title={'Add Activity'} />

      <ProgressIndicator
        currentStep={1}
        completed={false}
        steps={2}
        titles={[{title: 'Setup'}, {title: 'Review'}]}
        onStepClick={step => {
          if (step === 2) {
            navigate(ROUTES.ACTIVITY_REVIEW)
          }
        }}
      />
      <br />
      <ActivitySetUp 
        localCurrencyCode={localCurrencyCode} 
        showToast={showToast}
        toastMessage={toastMessage}
        handleToastClose={handleToastClose}
        handleViewActivity={handleViewActivity}
      />

      {isCancel ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          onCloseModal={handleCancel}
          title="Are you sure you want to cancel?"
          text={'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'}
          showPrimaryButton={true}
          primaryButtonText="Yes"
          onClickPrimaryButton={onClickPrimaryButton}
          showSecondaryButton={true}
          secondaryButtonText="No"
          onClickSecondaryButton={onClickSecondaryButton}
          footerProps={{
            size: 'medium',
            type: 'primary',
          }}
          isDark={true}
        />
      ) : null}

      {!isNameValid && (
          <Modal
            type={MODAL_TYPE.FLUID}
            showModal={!isNameValid}
            onCloseModal={handleValidationModalClose}
            title="Duplicate Name Detected"
            text={
              <>
              It looks like there's already an Activity with this name.<br />
              To avoid confusion, please enter a unique name before continuing.
              </>
                }
            showPrimaryButton={true}
            primaryButtonText="Edit Name"
            onClickPrimaryButton={handleValidationModalClose}
            showSecondaryButton={false}
            footerProps={{
              size: 'medium',
              type: 'primary',
            }}
            isDark={true}
            hideCloseButton={true}
          />
        )}

      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleDraft}
        FormError={validateFormData({ validateStartDate: !isPublishedStatus })}
        hideSaveDraft={handleHideSaveDraft()}
        disableDraft={isPublishedStatus}
      />
    </>
  )
}
export default AddActivity
